import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import CloseIcon from '@mui/icons-material/Close';
import { BrowserMultiFormatReader } from '@zxing/library';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import {
  loadSavedCameraDeviceId,
  saveCameraDeviceId,
  clearCameraDeviceId,
} from '../../helpers/cameraDeviceStorage';
import {
  terminalColors,
  terminalBarIconButtonSx,
  terminalSelectSx,
  terminalSelectMenuProps,
} from '../../theme/terminalStyles';

const dialogPaperSx = {
  bgcolor: terminalColors.gradientTop,
  backgroundImage: 'none',
  borderRadius: 2,
  m: { xs: 1, sm: 2 },
};

const dialogTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  fontWeight: 600,
  color: terminalColors.textPrimary,
  pr: 6,
};

/**
 * Сканування штрихкоду / QR з камери. Після успішного зчитування викликає onCode(text) і закривається.
 */
const BarcodeCameraDialog = ({ open, onClose, onCode, title = 'Сканувати з камери' }) => {
  const videoRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [error, setError] = useState('');
  const [torchOn, setTorchOn] = useState(false);

  const selectDevice = useCallback((id) => {
    setDeviceId(id);
    if (id) saveCameraDeviceId(id);
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      const reader = new BrowserMultiFormatReader();
      const inputs = await reader.listVideoInputDevices();
      setDevices(inputs);

      if (!inputs.length) {
        setDeviceId('');
        return;
      }

      const saved = loadSavedCameraDeviceId();
      const found = saved && inputs.some((d) => d.deviceId === saved);
      if (found) {
        setDeviceId(saved);
        return;
      }

      if (saved) clearCameraDeviceId();

      const next = inputs[0].deviceId;
      selectDevice(next);
    } catch (e) {
      setError('Не вдалося отримати список камер');
    }
  }, [selectDevice]);

  useEffect(() => {
    if (!open) {
      setError('');
      setTorchOn(false);
      setDevices([]);
      setDeviceId('');
      return undefined;
    }
    loadDevices();
    return undefined;
  }, [open, loadDevices]);

  useEffect(() => {
    if (!open) return undefined;

    const video = videoRef.current;
    if (!video) return undefined;

    const reader = new BrowserMultiFormatReader();
    let finished = false;

    const run = async () => {
      setError('');
      try {
        await reader.decodeFromVideoDevice(deviceId || undefined, video, (result) => {
          if (finished) return;
          if (result) {
            finished = true;
            try {
              reader.reset();
            } catch (e) {
              /* ignore */
            }
            onCode(result.getText());
            onClose();
          }
        });
      } catch (e) {
        if (!finished) setError(String(e?.message || e));
      }
    };

    const t = setTimeout(run, 150);

    return () => {
      finished = true;
      clearTimeout(t);
      try {
        reader.reset();
      } catch (e) {
        /* ignore */
      }
    };
  }, [open, deviceId, onCode, onClose]);

  const getVideoTrack = () => {
    const stream = videoRef.current?.srcObject;
    if (!stream?.getVideoTracks) return null;
    return stream.getVideoTracks()[0] || null;
  };

  const toggleTorch = async () => {
    const track = getVideoTrack();
    if (!track?.applyConstraints) {
      setError('Спалах недоступний');
      return;
    }
    try {
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      setError('Спалах не підтримується');
    }
  };

  const switchCamera = () => {
    if (devices.length < 2) return;
    const idx = Math.max(0, devices.findIndex((d) => d.deviceId === deviceId));
    selectDevice(devices[(idx + 1) % devices.length].deviceId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <PhotoCameraIcon sx={{ color: terminalColors.textPrimary }} />
        {title}
        <IconButton
          aria-label="закрити"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            ...terminalBarIconButtonSx,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {error ? (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        ) : null}
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#000',
            borderRadius: 1.5,
            overflow: 'hidden',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.12)',
          }}
        >
          <video ref={videoRef} style={{ width: '100%', display: 'block' }} playsInline muted />
        </Box>
        {devices.length > 0 ? (
          <FormControl fullWidth margin="normal" size="small" sx={terminalSelectSx}>
            <InputLabel id="cam-select">Камера</InputLabel>
            <Select
              labelId="cam-select"
              label="Камера"
              value={deviceId}
              onChange={(e) => selectDevice(e.target.value)}
              MenuProps={terminalSelectMenuProps}
            >
              {devices.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label || d.deviceId.slice(0, 12)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <IconButton
            aria-label="перемкнути камеру"
            onClick={switchCamera}
            disabled={devices.length < 2}
            sx={terminalBarIconButtonSx}
          >
            <CameraswitchIcon />
          </IconButton>
          <IconButton aria-label="спалах" onClick={toggleTorch} sx={terminalBarIconButtonSx}>
            {torchOn ? <FlashlightOffIcon /> : <FlashlightOnIcon />}
          </IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            Наведіть камеру на штрихкод
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <TerminalOrangeButton fullWidth size="large" onClick={onClose}>
          Закрити
        </TerminalOrangeButton>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeCameraDialog;
